
import {
  defineComponent, ref, watch, computed, onMounted, onBeforeUnmount,
} from '@vue/composition-api';
import { FCCart, LinkButton } from '@fc/angie-ui';
import { useCartContext } from '~/composables/useCartContext';
import { useNavbarScrollBehavior } from '~/composables/useNavbarScrollBehavior';
import { isMobileEffect } from '~/effects/matchMedia';
import navTypes from '~/store/constants/navTypes';

export default defineComponent({
  name: 'DefaultNavigationBar',
  components: {
    FCCart,
    LinkButton,
    CartDrawer: () => import('~/components/checkout/components/CartDrawer.vue'),
    MobileNavigationDrawer: () => import('~/components/checkout/components/MobileNavigationDrawer.vue'),
  },
  setup() {
    const { $store } = useNuxtApp();

    const navigationData = computed(() => [
      {
        cta: 'Home',
        elementName: 'NuxtLink',
        bindings: {
          to: '/',
        },
      },
      {
        cta: 'The System',
        elementName: 'NuxtLink',
        bindings: {
          to: '/the-system',
        },
      },
      {
        cta: 'The Results',
        elementName: 'NuxtLink',
        bindings: {
          to: '/the-results',
        },
      },
      // {
      //   cta: 'Pricing',
      //   elementName: 'NuxtLink',
      //   bindings: {
      //     to: '/pricing',
      //   },
      // },
    ]);

    const currentRoute = computed(() => $store.getters.currentRoute);
    watch(currentRoute, (newVal) => {
      if (newVal) {
        isMenuOpen.value = false;
      }
    }, { deep: true });

    function getLinkName(elName: string) {
      return `${currentRoute.value.name}_NavigationBar_${elName}`;
    }

    const isMenuOpen = ref(false);
    function mobileMenuToggle() { isMenuOpen.value = !isMenuOpen.value; }

    function toggleDrawer() { $store.dispatch('toggleDrawer'); }

    const { cartqty, cartDrawerOpen } = useCartContext();
    const showCart = computed(() => {
      const regex = /^\/shop\/(products|accessories|members)/;
      return regex.test(currentRoute.value.path);
    });

    // Product page theme detection
    const isProductPage = computed(() => {
      const productRegex = /^\/shop\/products/;
      return productRegex.test(currentRoute.value.path);
    });

    const isShopPage = computed(() => currentRoute.value.name === 'shop');
    const navType = computed(() => $store.getters.navType);
    const isErrorPage = computed(() => navType.value === navTypes.error);

    const theme = computed(() => {
      if (isProductPage.value || isErrorPage.value) return 'light';
      return 'dark';
    });

    // Scroll behavior for hiding/showing navbar and sale banner
    const {
      isNavVisible,
      isSaleBannerVisible,
      hasScrolledBackground,
      saleBannerRef,
      saleBannerHeight,
    } = useNavbarScrollBehavior({
      scrollThreshold: isProductPage.value ? 30 : 70,
      autoHideDelay: 2000,
      showOnScrollUpDistance: 50,
    });

    const isMobile = isMobileEffect();

    // Track client-side hydration for SSR-safe reactive properties
    const isClientHydrated = ref(false);

    // Geolocation helpers
    const isGeolocatedCanada = computed(() => isClientHydrated.value && $store.getters.isGeoLocatedCanada);

    // Rotating banner messages
    const bannerMessages = computed(() => {
      const messages: string[] = [];

      // Default to US content during SSR
      if (!isClientHydrated.value || !isGeolocatedCanada.value) {
        messages.push('<span class="fc-bold">FightCamp is HSA/FSA Eligible:</span> Save an average of 30%.');
      } else {
        messages.push('<span class="fc-bold">FightCamp now available in Canada!</span>');
      }

      return messages;
    });

    const currentBannerIndex = ref(0);
    const bannerMessage = computed(() => bannerMessages.value[currentBannerIndex.value]);

    // Red banner only on PDP and Shop pages for Canada
    const showRedBanner = computed(() => isGeolocatedCanada.value && (isProductPage.value || isShopPage.value));

    let bannerInterval: ReturnType<typeof setInterval> | null = null;

    onMounted(() => {
      isClientHydrated.value = true;

      // Start rotating banner messages every 5 seconds
      bannerInterval = setInterval(() => {
        currentBannerIndex.value = (currentBannerIndex.value + 1) % bannerMessages.value.length;
      }, 5000);
    });

    // Clean up interval on component unmount
    onBeforeUnmount(() => {
      if (bannerInterval) {
        clearInterval(bannerInterval);
      }
    });

    // Remove focus from clicked elements on mobile to prevent focus retention
    const handleLinkClick = (event: Event) => {
      if (isMobile.value && event.target instanceof HTMLElement) {
        event.target.blur();
      }
    };

    // Estimated banner height for SSR (before actual measurement)
    const estimatedBannerHeight = 40;

    // Button size - default to x-small for SSR to prevent layout shift
    const buttonSize = computed(() => {
      if (!isClientHydrated.value) return 'x-small';
      return isMobile.value ? 'x-small' : 'medium';
    });

    // Banner visibility logic
    const showBanner = computed(() => {
      if (!isClientHydrated.value) return true; // Show by default during SSR
      // Show for Canada on all pages, or for US on non-PDP/shop pages
      return isGeolocatedCanada.value || (!isProductPage.value && !isShopPage.value);
    });

    const hideStripe = computed(() => {
      if (!isClientHydrated.value) return false; // Show stripe by default during SSR
      // Hide stripe only for Canada on PDP and shop pages
      return isGeolocatedCanada.value && (isProductPage.value || isShopPage.value);
    });

    const navbarTopPosition = computed(() => {
      if (!isSaleBannerVisible.value) return '0';
      if (!showBanner.value) return '0';

      const height = saleBannerHeight.value > 0 ? saleBannerHeight.value : estimatedBannerHeight;
      return `${height}px`;
    });

    return {
      isMenuOpen,
      getLinkName,
      mobileMenuToggle,
      toggleDrawer,
      cartqty,
      currentRoute,
      navigationData,
      showCart,
      isNavVisible,
      isSaleBannerVisible,
      hasScrolledBackground,
      isProductPage,
      isMobile,
      handleLinkClick,
      cartDrawerOpen,
      isShopPage,
      saleBannerRef,
      saleBannerHeight,
      navbarTopPosition,
      buttonSize,
      theme,
      bannerMessage,
      showRedBanner,
      showBanner,
      hideStripe,
    };
  },
});

